import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { apiClient } from 'lib/api';
import type { QueryConfig } from 'lib/react-query';
import queryKeys from './queryKeys';
import type { TwitterAdSettingsResponse } from './types';

export interface GetTwitterAdSettingsParams {
  app: string;
  flows: string[];
}

export const getTwitterAdSettings = async (
  { params }: { params: GetTwitterAdSettingsParams },
  signal?: AbortSignal,
): Promise<TwitterAdSettingsResponse> => {
  const res = await apiClient.post<TwitterAdSettingsResponse>(
    queryKeys.adSettings,
    { app: params.app, flows: params.flows },
    { signal },
  );

  if (isAxiosError(res)) {
    const error = new Error('Get X Ad Settings Failed');
    error.cause = res.response?.data;
    return Promise.reject(error);
  }

  return res.data;
};

interface UseTwitterAdSettingsOptions<TData> {
  params: GetTwitterAdSettingsParams;
  config?: QueryConfig<typeof getTwitterAdSettings, TData>;
}

export const useTwitterAdSettings = <TData = TwitterAdSettingsResponse>({
  params,
  config,
}: UseTwitterAdSettingsOptions<TData>) =>
  useQuery({
    staleTime: 0,
    refetchOnMount: true,
    retry: false,
    ...config,
    enabled: (config?.enabled ?? true) && !!params.app && params.flows.length > 0,
    queryFn: ({ signal }) => getTwitterAdSettings({ params }, signal),
    queryKey: [queryKeys.adSettings, params.app, params.flows],
  });
